// src/components/ManageQuoteActions.jsx
import React, { useState, useEffect } from 'react';
import { Brain, UploadCloud, History } from 'lucide-react';

/**
 * Component that displays buttons to analyze a quote with Claude AI and publish it
 * This is meant to be added to the framework tab
 */
const ManageQuoteActions = ({ quoteId }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (quoteId) {
      setLoading(true);
      fetch(`/api/quotes/${quoteId}`)
        .then(res => res.json())
        .then(data => {
          setQuote(data);
          setLoading(false);
        })
        .catch(err => {
          console.error("Error fetching quote:", err);
          setLoading(false);
        });
    }
  }, [quoteId]);
  
  const handlePublish = () => {
    if (!quoteId) return;
    
    fetch(`/api/admin/quotes/${quoteId}/publish`, {
      method: 'POST'
    })
    .then(response => {
      if (!response.ok) throw new Error('Failed to publish quote');
      return response.json();
    })
    .then(() => {
      alert("Quote published successfully!");
      // Refresh the quote data
      fetch(`/api/quotes/${quoteId}`)
        .then(res => res.json())
        .then(data => {
          setQuote(data);
        });
    })
    .catch(error => {
      console.error('Error publishing quote:', error);
      alert(`Error publishing quote: ${error.message}`);
    });
  };
  
  if (!quoteId) return null;
  if (loading) return <div className="mt-2 text-sm text-gray-500">Loading...</div>;
  
  return (
    <div className="mt-2 space-y-2">
      <a 
        href={`/admin/claude-analyze?quoteId=${quoteId}`}
        className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
      >
        <Brain className="h-4 w-4 mr-2" />
        Analyze with Claude AI
      </a>
      
      <button
        onClick={() => {
          console.log(`Adding test record for quote ${quoteId}`);
          fetch(`/api/admin/quote-analysis-history?quoteId=${quoteId}&addTest=true`)
            .then(res => {
              console.log("Add record response:", res.status, res.statusText);
              return res.json();
            })
            .then(data => {
              console.log("Add test record result:", data);
              
              if (data.test_result && data.test_result.success) {
                alert(`Test record added successfully!\n\nRecord ID: ${data.test_result.record.id}\nModel: ${data.test_result.record.model_used}\nCreated: ${new Date(data.test_result.record.created_at).toLocaleString()}`);
              } else {
                alert(`Failed to add test record: ${data.test_result ? data.test_result.error : 'Unknown error'}`);
              }
              
              // Immediately check if record is visible
              return fetch(`/api/admin/quote-analysis-history?quoteId=${quoteId}`);
            })
            .then(res => res.json())
            .then(historyData => {
              console.log("History check after adding test:", historyData);
              
              const recordsFound = historyData.has_records ? 
                `Found ${historyData.record_count} records!` : 
                'No records found after adding test.';
                
              alert(`Test record verification: ${recordsFound}\n\nThis indicates whether your test record was properly saved AND can be retrieved.`);
            })
            .catch(err => {
              console.error("Error with test record:", err);
              alert(`Error with test record: ${err.message}`);
            });
        }}
        className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
      >
        Add & Verify Test Record
      </button>
      
      <button 
        onClick={() => {
          // Use the admin API directly
          console.log(`Fetching history for quote ${quoteId}...`);
          
          fetch(`/api/admin/quote-analysis-history?quoteId=${quoteId}`)
            .then(res => {
              console.log("History fetch response:", res.status, res.statusText);
              if (!res.ok) {
                throw new Error(`History fetch failed: ${res.status} ${res.statusText}`);
              }
              return res.json();
            })
            .then(data => {
              console.log("History data received:", data);
              
              // Show metadata checks first
              const metadataInfo = data.metadata_checks ? 
                `Metadata Table Status:
- Table exists: ${data.metadata_checks.table_exists ? 'Yes' : 'No'}
- Records count: ${data.metadata_checks.records_count || 0}
- Schema OK: ${data.metadata_checks.schema && data.metadata_checks.schema.length > 0 ? 'Yes' : 'No'}\n` :
                'No metadata checks available\n';
              
              if (data.success && data.analysis_history && data.analysis_history.length > 0) {
                const historyText = data.analysis_history.map(item => 
                  `Model: ${item.model_used}\nTime: ${new Date(item.created_at).toLocaleString()}\n` +
                  (item.error_reason ? `Error: ${item.error_reason}\n` : '')
                ).join('\n---\n\n');
                
                alert(`${metadataInfo}\nAnalysis History for Quote #${quoteId}:\n\n${historyText}`);
              } else if (data.message) {
                alert(`${metadataInfo}\nMessage from server: ${data.message}`);
              } else {
                // Provide diagnostic info and options
                const confirmCheck = window.confirm(`${metadataInfo}\nNo analysis history found for quote #${quoteId}. Would you like to run a full database check?`);
                
                if (confirmCheck) {
                  // Get full database status
                  fetch(`/api/status`)
                    .then(res => res.json())
                    .then(statusData => {
                      console.log("Database status:", statusData);
                      
                      const statusInfo = `
Database Status:
--------------
Tables: ${statusData.tables && statusData.tables.list ? statusData.tables.list.join(', ') : 'None found'}
Metadata table exists: ${statusData.tables && statusData.tables.claude_analysis_metadata ? 'Yes' : 'No'}
Total metadata records: ${statusData.analysis_metadata ? statusData.analysis_metadata.count : 'Unknown'}
Insert test: ${statusData.insert_test ? (statusData.insert_test.success ? 'Success' : 'Failed') : 'Not run'}
Insert error: ${statusData.insert_test && statusData.insert_test.error ? statusData.insert_test.error : 'None'}

Do you want to add a test record to verify database functionality?
                      `;
                      
                      if (window.confirm(statusInfo)) {
                        // Add a test record
                        fetch(`/api/admin/quote-analysis-history?quoteId=${quoteId}&addTest=true`)
                          .then(res => res.json())
                          .then(testResult => {
                            alert(`Test record added: ${testResult.test_result && testResult.test_result.success ? 'Success' : 'Failed'}\n\nCheck the history again to see if it appears.`);
                          })
                          .catch(err => {
                            alert(`Error adding test record: ${err.message}`);
                          });
                      }
                    })
                    .catch(err => {
                      console.error("Database status check error:", err);
                      alert(`Database status check failed: ${err.message}`);
                    });
                } else {
                  alert('No analysis history found for this quote');
                }
              }
            })
            .catch(err => {
              console.error("Error fetching analysis history:", err);
              alert(`Error fetching analysis history: ${err.message}`);
            });
        }}
        className="flex items-center justify-center w-full px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
      >
        <History className="h-4 w-4 mr-2" />
        View Analysis History
      </button>
      
      {quote && quote.hasOwnProperty('published') && (
        <button
          onClick={handlePublish}
          disabled={quote.published}
          className={`flex items-center justify-center w-full px-4 py-2 border border-transparent text-sm font-medium rounded-md 
            ${quote.published 
              ? 'text-green-800 bg-green-100 cursor-not-allowed' 
              : 'text-white bg-blue-600 hover:bg-blue-700'}`}
        >
          <UploadCloud className="h-4 w-4 mr-2" />
          {quote.published ? 'Published' : 'Publish Quote'}
        </button>
      )}
    </div>
  );
};

export default ManageQuoteActions;